// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (C) 2026 Ben Richardson — https://benrichardson.dev
// Additional terms under AGPL-3.0 section 7(b) apply; see ADDITIONAL-TERMS.md.
/**
 * Bootstrap: resolve the pairing (from the link hash or freshly generated),
 * render the UI, and — once a folder is chosen — spin up the crypto worker and
 * the sync engine, wiring their events to the interface and the event log.
 */
import './styles/main.css';
import QRCode from 'qrcode';
import { AppUI } from './ui.ts';
import { initModals, toast } from './modals.ts';
import { initGlossary } from './glossary.ts';
import { emit } from './eventlog.ts';
import { readInvite, writeRoomHash, currentInviteUrl } from './router.ts';
import { generateRoomId, generatePassphrase, normalizePass } from './room.ts';
import { deriveSasWords } from './crypto.ts';
import { pickDirectory, supportsDirectoryPicker, DirectoryStore, FilesStore, MemStore, type FolderStore } from './fs.ts';
import { CryptoClient } from './cryptoclient.ts';
import { SyncEngine } from './sync.ts';
import type { Direction } from './types.ts';

const LS_DIR = 'syncwell.direction';
const LS_NAME = 'syncwell.name';

let room = '';
let pass = '';
let direction: Direction = 'both';
let deviceName = '';
let crypto: CryptoClient | null = null;
let engine: SyncEngine | null = null;
let started = false;
let peerName = '';

const ui = new AppUI(document.getElementById('app') as HTMLElement, {
  onChooseFolder: chooseFolder,
  onDropFolder: dropFolder,
  onChooseFiles: chooseFiles,
  onDirectionChange: (d) => {
    if (started) return;
    direction = d;
    localStorage.setItem(LS_DIR, d);
  },
  onNameChange: (n) => {
    if (started) return;
    deviceName = n;
    localStorage.setItem(LS_NAME, n);
  },
  onCopyLink: copyLink,
  onShareLink: shareLink,
  onDisconnect: disconnect,
});

init();

function init(): void {
  initModals();
  initGlossary();

  // Restore preferences.
  direction = (localStorage.getItem(LS_DIR) as Direction) || 'both';
  deviceName = localStorage.getItem(LS_NAME) || '';
  ui.setDirection(direction);
  ui.setName(deviceName);
  ui.setSupportsDirectory(supportsDirectoryPicker());

  // Resolve the pairing.
  const invite = readInvite();
  if (invite && invite.pass) {
    room = invite.room;
    pass = normalizePass(invite.pass);
  } else if (invite && !invite.pass) {
    room = invite.room;
    pass = '';
    toast('This link is missing its passphrase — ask for the full link.', 'err');
  } else {
    room = generateRoomId();
    pass = generatePassphrase();
    writeRoomHash(room, pass, true);
  }

  const url = currentInviteUrl(room, pass, true);
  ui.setInviteLink(url);
  void renderQr(url);
  void renderSas();

  window.addEventListener('pagehide', () => engine?.stop());
  emit('info', 'Ready. Open this link on your other device to pair.');

  // Automated two-tab verification harness (no native picker available headlessly).
  // Drives the REAL engine — real crypto, real relays — with an in-memory folder.
  (window as unknown as { __syncwellTest?: unknown }).__syncwellTest = {
    room: () => room,
    pass: () => pass,
    started: () => started,
    startWithMemory: (label: string, initial: Record<string, string>) => {
      const files: Record<string, { bytes: Uint8Array; mtime: number }> = {};
      let t = 1_000_000;
      for (const [p, text] of Object.entries(initial)) files[p] = { bytes: new TextEncoder().encode(text), mtime: t++ };
      const s = new MemStore(label, files);
      lastMemStore = s;
      void beginWithStore(s);
      return true;
    },
    snapshot: () => lastMemStore?.snapshot() ?? null,
  };
}

let lastMemStore: MemStore | null = null;

async function renderQr(url: string): Promise<void> {
  try {
    const dataUrl = await QRCode.toDataURL(url, {
      margin: 1,
      width: 300,
      color: { dark: '#0a0f14', light: '#eef4f8' },
    });
    ui.setQr(dataUrl);
  } catch {
    /* QR is a nicety; ignore failures */
  }
}

async function renderSas(): Promise<void> {
  if (!pass) return;
  try {
    const words = await deriveSasWords(pass, room);
    ui.setSas(words);
  } catch {
    /* ignore */
  }
}

async function chooseFolder(): Promise<void> {
  try {
    const s = await pickDirectory();
    await beginWithStore(s);
  } catch (e) {
    if ((e as Error).name !== 'AbortError') {
      toast('Could not open that folder.', 'err');
      emit('bad', `Folder pick failed: ${(e as Error).message}`);
    }
  }
}

async function dropFolder(item: DataTransferItem): Promise<void> {
  try {
    const getHandle = (item as DataTransferItem & { getAsFileSystemHandle?: () => Promise<FileSystemHandle> }).getAsFileSystemHandle;
    if (!getHandle) {
      toast('Drag-and-drop of folders isn’t supported here — click to pick.', 'err');
      return;
    }
    const handle = await getHandle.call(item);
    if (!handle || handle.kind !== 'directory') {
      toast('Please drop a folder, not a file.', 'err');
      return;
    }
    const perm = handle as unknown as { requestPermission?: (o: { mode: string }) => Promise<string> };
    if (perm.requestPermission) {
      const granted = await perm.requestPermission({ mode: 'readwrite' });
      if (granted !== 'granted') {
        toast('Write permission is needed to mirror this folder.', 'err');
        return;
      }
    }
    await beginWithStore(new DirectoryStore(handle as FileSystemDirectoryHandle));
  } catch (e) {
    emit('bad', `Drop failed: ${(e as Error).message}`);
  }
}

function chooseFiles(): void {
  const input = document.createElement('input');
  input.type = 'file';
  input.multiple = true;
  try {
    (input as HTMLInputElement & { webkitdirectory: boolean }).webkitdirectory = true;
  } catch {
    /* plain multi-file pick */
  }
  input.addEventListener('change', () => {
    const files = input.files ? Array.from(input.files) : [];
    if (files.length === 0) return;
    void beginWithStore(new FilesStore(files));
  });
  input.click();
}

async function beginWithStore(s: FolderStore): Promise<void> {
  if (started) return;
  if (!pass) {
    toast('This link is missing its passphrase.', 'err');
    return;
  }
  ui.setFolder(s.label);
  emit('good', `Folder selected: ${s.label} (${s.kind === 'directory' ? 'live mirror' : 'files'}).`);
  lockOptions();

  crypto = new CryptoClient(pass, room);
  engine = new SyncEngine({
    roomId: room,
    pass,
    store: s,
    crypto,
    direction,
    name: deviceName || (s.kind === 'directory' ? 'A device' : 'Files'),
    hooks: {
      log: (kind, text) => emit(kind, text),
      status: (st) => ui.setStatus(st, peerName),
      peer: (hello) => {
        peerName = hello?.name || '';
        ui.setPeer(hello);
      },
      localSummary: (f, b) => ui.setLocalSummary(f, b),
      remoteSummary: (f, b) => ui.setRemoteSummary(f, b),
      transfer: (row) => ui.upsertTransfer(row),
      extraPeer: () => ui.showExtraPeer(),
    },
  });
  started = true;
  try {
    await engine.start();
  } catch (e) {
    emit('bad', `Could not start: ${(e as Error).message}`);
    ui.setStatus('error');
  }
}

function lockOptions(): void {
  (document.getElementById('dir-select') as HTMLSelectElement).disabled = true;
  (document.getElementById('name-input') as HTMLInputElement).disabled = true;
}
function unlockOptions(): void {
  (document.getElementById('dir-select') as HTMLSelectElement).disabled = false;
  (document.getElementById('name-input') as HTMLInputElement).disabled = false;
}

function disconnect(): void {
  engine?.stop();
  crypto?.destroy();
  engine = null;
  crypto = null;
  started = false;
  peerName = '';
  ui.setStatus('idle');
  ui.setPeer(null);
  ui.resetFolderPicker();
  unlockOptions();
  emit('warn', 'Disconnected. Choose a folder to reconnect.');
  toast('Disconnected.', 'info');
}

async function copyLink(): Promise<void> {
  try {
    await navigator.clipboard.writeText(currentInviteUrl(room, pass, true));
    toast('Pairing link copied.', 'ok');
  } catch {
    toast('Could not copy — select the link and copy manually.', 'err');
  }
}

async function shareLink(): Promise<void> {
  const url = currentInviteUrl(room, pass, true);
  if (navigator.share) {
    try {
      await navigator.share({ title: 'Syncwell', text: 'Sync a folder with me', url });
    } catch {
      /* user cancelled */
    }
  } else {
    void copyLink();
  }
}
